import { IColumnElement, IRowElement } from '@doseme/cohesive-ui'
import moment from 'moment'
import { IAggregatedEntitlement } from '../../types/hospital'
import { IPlan } from '../../types/plan'
import { ISubscription } from '../../types/subscription'

export function smartListData<T>(params: {
  all: Record<number, T>,
  ids: Array<string | number>,
  searchContent: Record<number, string[]>,
  searchText: string,
  columnizeRow: (entity: any) => IColumnElement[],
  getOnClickCallback?: (entityId: number) => any
}): IRowElement[] {
  const { all, ids, searchText, columnizeRow, getOnClickCallback, searchContent } = params

  return ids.reduce<IRowElement[]>((acc, id) => {
    const numericId = typeof id === 'number' ? id : parseInt(id, 10)
    if (
      !searchText ||
      (searchText && searchContent[numericId].some(text => text?.toLowerCase().includes(searchText.toLowerCase())))
    ) {
      if (!!getOnClickCallback) {
        return [
          ...acc,
          {
            id,
            columns: columnizeRow(all[numericId]),
            onClick: () => getOnClickCallback?.(typeof id === 'string' ? parseInt(id, 10) : id)
          }
        ]
      }

      return [
        ...acc,
        {
          id,
          columns: columnizeRow(all[numericId]),
        }
      ]
    }

    return acc
  }, [])
}

export const entitlementsForHospital = (
  plans: Record<number, IPlan>,
  subscriptions: ISubscription[]
): IAggregatedEntitlement[] => {
  const entitlements = subscriptions.reduce((acc: Record<string, IAggregatedEntitlement>, sub) => {
    if (moment().isBefore(sub.start) || moment().isAfter(sub.end)) {
      return acc
    }

    plans[sub.planId].drugs.forEach(drug => {
      if (!(drug.module in acc)) {
        acc[drug.module] = {
          name: drug.name,
          module: drug.module,
          maxCourseCount: 0
        }
      }

      // -1 is infinity, so this will set the entitlement count to -1.
      // The subsequent check ensures that once the value is -1,
      // it cannot ever become a finite (positive) number of entitlements.
      // We check this using x < 0 instead of x === -1 purely to be defensive.
      if (drug.maxCourseCount < 0) {
        acc[drug.module].maxCourseCount = -1
        return
      }

      if (acc[drug.module].maxCourseCount < 0) {
        return
      }

      acc[drug.module].maxCourseCount += drug.maxCourseCount
    })

    return acc
  }, {})

  return Object.values(entitlements)
}
